import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import Seo from '../../components/Seo';
import ShareListing from '../../components/ShareListing';
import PhotoLogoUploader from '../../components/PhotoLogoUploader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, CreditCard, Star, Gift, CheckCircle2, Clock, Trophy, Hourglass, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

const Bar = ({ value, max }) => (
  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
    <div className="h-full bg-secondary transition-all" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
  </div>
);

const MyFeatured = () => {
  const [data, setData] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [payFor, setPayFor] = useState(null); // membership for paid-continue dialog
  const [reqFor, setReqFor] = useState(null); // listing for the request-featured dialog
  const [reqImage, setReqImage] = useState('');

  const load = useCallback(async () => {
    try {
      const [me, el] = await Promise.all([
        axios.get(`${API_URL}/featured/me`),
        axios.get(`${API_URL}/featured/eligible-listings`),
      ]);
      setData(me.data);
      setListings(el.data.listings || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addCard = async () => {
    setBusy('card');
    try {
      const r = await axios.post(`${API_URL}/featured/card/setup-session`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch { toast.error('Could not start card setup'); setBusy(''); }
  };

  const removeCard = async () => {
    setBusy('card');
    try { await axios.delete(`${API_URL}/featured/card`); toast.success('Card removed'); await load(); }
    catch { toast.error('Could not remove card'); } finally { setBusy(''); }
  };

  const submitRequest = async () => {
    if (!reqFor) return;
    if (!reqImage) { toast.error('Please upload a professional photo or logo first.'); return; }
    setBusy(reqFor.listing_id);
    try {
      await axios.post(`${API_URL}/featured/request`, { listing_type: reqFor.listing_type, listing_id: reqFor.listing_id, image_url: reqImage });
      toast.success('Request submitted! You\u2019ll get a Congratulations email once an admin approves.');
      setReqFor(null); setReqImage('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit request'); } finally { setBusy(''); }
  };

  const endFeatured = async (m) => {
    setBusy(m.membership_id);
    try { await axios.post(`${API_URL}/featured/end/${m.membership_id}`); toast.success('Featured ended — your regular listing stays live.'); await load(); }
    catch { toast.error('Could not end featured'); } finally { setBusy(''); }
  };

  const doPaid = async () => {
    if (!payFor) return;
    setBusy(payFor.membership_id);
    try {
      const r = await axios.post(`${API_URL}/featured/continue-paid/${payFor.membership_id}`);
      toast.success(`Paid $${r.data.amount}. Featured until ${fmtDate(r.data.expires_at)}`);
      setPayFor(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); } finally { setBusy(''); }
  };

  if (loading) return <div className="min-h-screen pt-28 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const r = data?.reward || {};
  const pending = (data?.memberships || []).filter((m) => m.status === 'pending_approval');
  const rejected = (data?.memberships || []).filter((m) => m.status === 'rejected');
  const pendingKeys = new Set(pending.map((m) => `${m.listing_type}:${m.listing_id}`));
  const notFeatured = listings.filter((l) => !l.featured && !pendingKeys.has(`${l.listing_type}:${l.listing_id}`));

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16" data-testid="my-featured-page">
      <Seo title="My Featured Status" description="Manage your Featured placement on I Need A Social Worker." path="/my-featured" noindex />
      <section className="bg-primary pt-24 pb-8 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold flex items-center gap-2"><Star className="w-7 h-7 text-secondary" /> My Featured Status</h1>
          <p className="text-white/80 mt-2">Be Featured <strong>free for 30 days</strong>. Just upload a professional photo or logo and submit — once an admin approves, your listing is Featured and you'll get a Congratulations email. No card, resources, or referrals required.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Request Featured — upload photo/logo, submit for admin approval */}
        {notFeatured.length > 0 && (
          <div className="bg-white rounded-xl border p-6" data-testid="request-section">
            <h2 className="font-serif text-xl font-bold text-primary mb-1">Be Featured — Free for 30 Days</h2>
            <p className="text-sm text-gray-500 mb-4">Upload a professional photo or logo and submit. An admin approves it, then your listing is Featured for 30 days. $0 — no card required.</p>
            <div className="space-y-2">
              {notFeatured.map((l) => (
                <div key={`${l.listing_type}-${l.listing_id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{l.title}</p>
                    <p className="text-xs text-gray-400 capitalize">{l.listing_type.replace('_', ' ')}</p>
                  </div>
                  <Button size="sm" className="bg-secondary text-white" onClick={() => { setReqFor(l); setReqImage(''); }} data-testid={`request-${l.listing_id}`}>
                    Request Featured
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending / rejected requests */}
        {(pending.length > 0 || rejected.length > 0) && (
          <div className="bg-white rounded-xl border p-6" data-testid="requests-status">
            <h2 className="font-serif text-xl font-bold text-primary mb-3">Your Featured Requests</h2>
            <div className="space-y-2">
              {pending.map((m) => (
                <div key={m.membership_id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`pending-${m.membership_id}`}>
                  {m.photo_url && <img src={m.photo_url} alt="" className="w-10 h-10 rounded object-cover" />}
                  <div className="flex-1"><p className="font-medium text-gray-900">{m.listing_title}</p><p className="text-xs text-amber-700 flex items-center gap-1"><Hourglass className="w-3 h-3" /> Awaiting admin approval</p></div>
                </div>
              ))}
              {rejected.map((m) => (
                <div key={m.membership_id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid={`rejected-${m.membership_id}`}>
                  <XCircle className="w-5 h-5 text-red-500" />
                  <div className="flex-1"><p className="font-medium text-gray-900">{m.listing_title}</p><p className="text-xs text-red-700">{m.reject_reason || 'Please upload a different photo and resubmit.'}</p></div>
                  <Button size="sm" variant="outline" onClick={() => { const l = listings.find((x) => x.listing_id === m.listing_id) || { listing_type: m.listing_type, listing_id: m.listing_id, title: m.listing_title }; setReqFor(l); setReqImage(''); }} data-testid={`resubmit-${m.membership_id}`}>Upload new & resubmit</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment method — OPTIONAL, only for paid continuation after 30 days */}
        <div className="bg-white rounded-xl border p-6" data-testid="card-section">
          <h2 className="font-serif text-xl font-bold text-primary flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5" /> Payment Method <span className="text-xs font-normal text-gray-400">(optional)</span></h2>
          <p className="text-sm text-gray-500 mb-3">Not needed for your free 30 days. Add a card now only if you want seamless paid continuation ($49/30 days) after the complimentary period.</p>
          {data?.card_on_file ? (
            <div className="flex flex-wrap items-center gap-3">
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Card on file</Badge>
              <span className="text-sm text-gray-600 capitalize">{data.card?.brand} •••• {data.card?.last4}</span>
              <Button variant="outline" size="sm" onClick={removeCard} disabled={busy === 'card'} data-testid="remove-card-btn">Remove card</Button>
            </div>
          ) : (
            <Button onClick={addCard} disabled={busy === 'card'} variant="outline" data-testid="add-card-btn">
              {busy === 'card' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add payment method (optional)'}
            </Button>
          )}
        </div>

        {/* Current featured listings */}
        <div className="bg-white rounded-xl border p-6" data-testid="featured-list">
          <h2 className="font-serif text-xl font-bold text-primary mb-4">Your Featured Listings</h2>
          {(data?.memberships || []).filter((m) => m.status === 'active').length === 0 ? (
            <p className="text-sm text-gray-500">No active Featured placements yet.</p>
          ) : (
            <div className="space-y-3">
              {data.memberships.filter((m) => m.status === 'active').map((m) => (
                <div key={m.membership_id} className="p-4 border rounded-lg" data-testid={`membership-${m.membership_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-semibold text-gray-900">{m.listing_title}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1"><Clock className="w-4 h-4" /> Featured until <strong>{fmtDate(m.expires_at)}</strong></p>
                      <Badge className="mt-1 bg-secondary/15 text-secondary border-secondary/30 capitalize">{m.period_type}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-primary text-white" onClick={() => setPayFor(m)} data-testid={`continue-${m.membership_id}`}>Continue Featured — $49/mo</Button>
                      <Button size="sm" variant="outline" onClick={() => endFeatured(m)} disabled={busy === m.membership_id} data-testid={`end-${m.membership_id}`}>End Featured</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Earn more featured time */}
        <div className="bg-white rounded-xl border p-6" data-testid="earn-more-section">
          <h2 className="font-serif text-xl font-bold text-primary flex items-center gap-2 mb-1"><Gift className="w-5 h-5 text-secondary" /> Earn Another 30 Days Featured</h2>
          <p className="text-sm text-gray-500 mb-4">Contribute <strong>{r.resources_required} approved Community Resources</strong> and refer <strong>{r.referrals_required} verified students/interns</strong> to earn another 30 days free. An admin confirms your activity before the reward is granted.</p>

          {r.reward_pending_admin && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2" data-testid="reward-pending">
              <Trophy className="w-4 h-4" /> Reward earned — awaiting admin approval of your 30-day extension.
            </div>
          )}

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1"><span className="font-medium text-gray-700">Approved Resources</span><span className="text-gray-500" data-testid="reward-resources">{r.reward_resources} of {r.resources_required}</span></div>
              <Bar value={r.reward_resources || 0} max={r.resources_required || 10} />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1"><span className="font-medium text-gray-700">Student/Intern Referrals</span><span className="text-gray-500" data-testid="reward-referrals">{r.reward_referrals} of {r.referrals_required}</span></div>
              <Bar value={r.reward_referrals || 0} max={r.referrals_required || 2} />
            </div>
          </div>

          {r.reward_ready && !r.reward_pending_admin && (
            <p className="mt-3 text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Requirements met!</p>
          )}

          <div className="mt-5 grid sm:grid-cols-3 gap-3 text-center">
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-2xl font-bold text-primary" data-testid="lifetime-resources">{r.lifetime_resources}</p><p className="text-xs text-gray-500">Lifetime Resources</p></div>
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-2xl font-bold text-primary" data-testid="lifetime-referrals">{r.lifetime_referrals}</p><p className="text-xs text-gray-500">Lifetime Referrals</p></div>
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-2xl font-bold text-secondary" data-testid="rewards-earned">{r.rewards_earned}</p><p className="text-xs text-gray-500">Rewards Earned</p></div>
          </div>

          {/* Referral link */}
          <div className="mt-6 pt-5 border-t">
            <p className="text-sm font-semibold text-primary mb-1">Your INASW Referral Link</p>
            <p className="text-xs text-gray-500 mb-2">Send this to students/interns. When they create an INASW account, it counts toward your reward.</p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="text-xs bg-gray-100 px-3 py-2 rounded break-all" data-testid="referral-link">{r.referral_link}</code>
            </div>
            <div className="mt-3"><ShareListing url={r.referral_link} title="Join me on I Need A Social Worker" /></div>
          </div>
        </div>
      </div>

      {/* Paid continuation dialog */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="bg-white" data-testid="paid-dialog">
          <DialogHeader><DialogTitle>Continue Featured Placement</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700 space-y-2">
            <p>Your complimentary period is ending. Continuing Featured placement is a paid option.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Featured price:</strong> $49.00</li>
              <li><strong>Billing:</strong> one 30-day period, charged to your card on file now</li>
              <li><strong>New renewal date:</strong> 30 days from your current expiration</li>
              <li>You can End Featured anytime; your regular listing stays live.</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={doPaid} disabled={busy === payFor?.membership_id} data-testid="confirm-paid">
              {busy === payFor?.membership_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pay $49 & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Featured dialog — upload photo/logo then submit for admin approval */}
      <Dialog open={!!reqFor} onOpenChange={(o) => { if (!o) { setReqFor(null); setReqImage(''); } }}>
        <DialogContent className="bg-white" data-testid="request-dialog">
          <DialogHeader><DialogTitle>Request Featured — {reqFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-gray-700">
            <p>Upload a professional photo or your organization/business logo. Your request goes to an admin for approval — once approved, you're Featured free for 30 days and we'll email you a Congratulations with your share link.</p>
            <PhotoLogoUploader value={reqImage} onChange={setReqImage} label="Professional Photo / Organization Logo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReqFor(null); setReqImage(''); }}>Cancel</Button>
            <Button className="bg-secondary text-white" onClick={submitRequest} disabled={!reqImage || busy === reqFor?.listing_id} data-testid="submit-request">
              {busy === reqFor?.listing_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyFeatured;
